"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createNewEnrollmentId = exports.FluentEnrollmentContextBuilder = void 0;
const uuid_1 = require("uuid");
const faker_1 = __importDefault(require("faker"));
const enrollment_1 = require("../v1/enrollment");
const create_new_enrollment_1 = require("./create-new-enrollment");
Object.defineProperty(exports, "createNewEnrollmentId", { enumerable: true, get: function () { return create_new_enrollment_1.createNewEnrollmentId; } });
/**
 * @export
 * @class FluentEnrollmentContextBuilder
 */
class FluentEnrollmentContextBuilder {
    /**
     * Creates an instance of FluentEnrollmentContextBuilder.
     * @param {EnrollmentStepsApi} enrollmentStepsClient
     * @param {EnrollmentApi} enrollmentClient
     * @param {() => string} getXCLEARCorrelationInfo
     * @param {number} [timeout]
     * @param {boolean} [suppressLogs] Does not suppress errors
     * @param {boolean} [suppressErrors]
     * @memberof FluentEnrollmentContextBuilder
     */
    constructor(enrollmentStepsClient, enrollmentClient, getXCLEARCorrelationInfo, timeout, suppressLogs, suppressErrors) {
        this.steps = [];
        this.suppressLogs = false;
        this.suppressErrors = false;
        this.logStep = (step) => {
            if (!this.suppressLogs)
                console.log('Step:', step);
        };
        this.logError = (...errors) => {
            if (!this.suppressErrors)
                console.log(...errors);
        };
        this.eligibility = (data) => {
            this.steps.push((config) => {
                this.logStep('eligibility');
                return this.enrollmentStepsClient.confirmEligibility({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    preEligibilityStep: {
                        eligibleForMembership: true,
                        ...data,
                    },
                });
            });
            return this;
        };
        this.citizenship = (data) => {
            this.steps.push((config) => {
                this.logStep('citizenship');
                return this.enrollmentStepsClient.reportCitizenship({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    preCitizenshipStep: {
                        birthCountry: 'US',
                        birthLocality: 'New York',
                        birthSubdivision: 'NY',
                        citizenshipCountry: 'US',
                        ...data,
                    },
                });
            });
            return this;
        };
        this.documentCheck = (data) => {
            this.steps.push((config) => {
                this.logStep('documentCheck');
                return this.enrollmentStepsClient.chooseDocuments({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    preDocumentCheckStep: {
                        photoId: 'PP',
                        document: null,
                        namesOnDocumentsMatch: null,
                        ...data,
                    },
                });
            });
            return this;
        };
        this.biographicInfo = (data) => {
            this.steps.push((config) => {
                this.logStep('biographicInfo');
                return this.enrollmentStepsClient.biographicInfo({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    preBiographicInfoStep: {
                        firstName: faker_1.default.name.firstName(),
                        noMiddleName: false,
                        middleName: 'James',
                        lastName: faker_1.default.name.lastName(),
                        gender: 'M',
                        dateOfBirth: new Date('1959-04-21'),
                        suffix: 'SR',
                        ...data,
                    },
                });
            });
            return this;
        };
        this.identityHints = (data) => {
            this.steps.push((config) => {
                this.logStep('identityHints');
                return this.enrollmentStepsClient.giveIdentityHints({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    preIdentityStep: {
                        everUsedAlias: true,
                        everUsedPreviousName: true,
                        ...data,
                    },
                });
            });
            return this;
        };
        this.attributes = (data) => {
            this.steps.push((config) => {
                this.logStep('attributes');
                return this.enrollmentStepsClient.provideAttributes({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    preAttributesStep: {
                        previousNames: [
                            {
                                firstName: faker_1.default.name.firstName(),
                                middleName: 'Romelle',
                                noMiddleName: false,
                                lastName: `PreviousLast${faker_1.default.name.lastName()}`,
                                suffix: 'senior',
                            },
                        ],
                        aliases: [
                            {
                                firstName: 'Trent',
                                middleName: 'John',
                                noMiddleName: false,
                                lastName: `AliasLast${faker_1.default.name.lastName()}`,
                            },
                        ],
                        eyeColor: 'brown',
                        hairColor: 'brown',
                        heightFeet: 6,
                        heightInches: 1,
                        weightPounds: 185,
                        ...data,
                    },
                });
            });
            return this;
        };
        this.addresses = (data) => {
            this.steps.push((config) => {
                this.logStep('addresses');
                return this.enrollmentStepsClient.provideAddresses({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    preAddressesStep: {
                        mailingAddress: {
                            country: 'CA',
                            lineOne: faker_1.default.address.streetAddress(),
                            locality: 'Redmond',
                            subdivision: 'AB',
                            postalCode: '12313131',
                        },
                        useDifferentResidentialAddress: true,
                        atCurrentAddressMoreThanFiveYears: false,
                        residentialAddress: {
                            country: 'US',
                            lineOne: faker_1.default.address.streetAddress(),
                            locality: 'Richmond',
                            subdivision: 'VA',
                            postalCode: '23059',
                        },
                        previousAddress: {
                            country: 'US',
                            lineOne: faker_1.default.address.streetAddress(),
                            locality: 'San Francisco',
                            subdivision: 'CA',
                            postalCode: '12313131',
                        },
                        ...data,
                    },
                });
            });
            return this;
        };
        this.contact = (data) => {
            this.steps.push((config) => {
                this.logStep('contact');
                const email = `dev${uuid_1.v4()}@test.com`;
                return this.enrollmentStepsClient.provideContact({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    preContactStep: {
                        email,
                        emailVerification: email,
                        phoneNumber: '+64.933908522',
                        preferredMethodOfContact: 'text',
                        optIntoReceivingTexts: true,
                        ...data,
                    },
                });
            });
            return this;
        };
        this.legalQuestionnaire = (data) => {
            this.steps.push((config) => {
                this.logStep('legalQuestionnaire');
                return this.enrollmentStepsClient.answerLegalQuestionnaire({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    preLegalQuestionnaireStep: {
                        usCitizenOrNationalOrLPR: true,
                        disqualifyingFelonyPartA: false,
                        disqualifyingFelonyPartB: false,
                        releasedFromIncarcerationPartB: false,
                        wantedOrUnderIndictment: false,
                        mentalCapacityLegalHistory: false,
                        ...data,
                    },
                });
            });
            return this;
        };
        this.finalize = (data) => {
            this.steps.push((config) => {
                this.logStep('finalize');
                return this.enrollmentStepsClient.finalizePreEnrollment({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    preFinalizeStepCommand: {
                        enrollmentLocationId: config.enrollmentId,
                        creditCard: {
                            number: '4111111111111111',
                            expiration: `01/${new Date().getFullYear() + 5}`,
                            cvv: '123',
                            billingZip: '78701',
                            cardType: enrollment_1.CreditCardCardTypeEnum.Visa,
                        },
                        ...data,
                    },
                });
            });
            return this;
        };
        this.describeHands = (data) => {
            this.steps.push((config) => {
                this.logStep('describeHands');
                return this.enrollmentStepsClient.describeHands({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    handsModel: {
                        left: {
                            amputated: false,
                            fingers: [
                                {
                                    finger: enrollment_1.FingerDescriptionFingerEnum.THUMB,
                                    description: enrollment_1.FingerDescriptionDescriptionEnum.PRESENT,
                                },
                                {
                                    finger: enrollment_1.FingerDescriptionFingerEnum.INDEX,
                                    description: enrollment_1.FingerDescriptionDescriptionEnum.PRESENT,
                                },
                                {
                                    finger: enrollment_1.FingerDescriptionFingerEnum.MIDDLE,
                                    description: enrollment_1.FingerDescriptionDescriptionEnum.PRESENT,
                                },
                                {
                                    finger: enrollment_1.FingerDescriptionFingerEnum.RING,
                                    description: enrollment_1.FingerDescriptionDescriptionEnum.PRESENT,
                                },
                                {
                                    finger: enrollment_1.FingerDescriptionFingerEnum.LITTLE,
                                    description: enrollment_1.FingerDescriptionDescriptionEnum.PRESENT,
                                },
                            ],
                        },
                        right: {
                            amputated: false,
                            fingers: [
                                {
                                    finger: enrollment_1.FingerDescriptionFingerEnum.THUMB,
                                    description: enrollment_1.FingerDescriptionDescriptionEnum.PRESENT,
                                },
                                {
                                    finger: enrollment_1.FingerDescriptionFingerEnum.INDEX,
                                    description: enrollment_1.FingerDescriptionDescriptionEnum.PRESENT,
                                },
                                {
                                    finger: enrollment_1.FingerDescriptionFingerEnum.MIDDLE,
                                    description: enrollment_1.FingerDescriptionDescriptionEnum.PRESENT,
                                },
                                {
                                    finger: enrollment_1.FingerDescriptionFingerEnum.RING,
                                    description: enrollment_1.FingerDescriptionDescriptionEnum.PRESENT,
                                },
                                {
                                    finger: enrollment_1.FingerDescriptionFingerEnum.LITTLE,
                                    description: enrollment_1.FingerDescriptionDescriptionEnum.PRESENT,
                                },
                            ],
                        },
                        ...data,
                    },
                });
            });
            return this;
        };
        this.uploadPhotoId = (data) => {
            this.steps.push(async (config, enrollment) => {
                var _a, _b;
                await this.enrollmentStepsClient.calculatePodPhotoIdScanStatus({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    podPhotoIdScanStatusForm: {
                        documentPurpose: enrollment_1.PodPhotoIdScanStatusDocumentPurposeEnum.CITIZENSHIP,
                        acuantResponse: {
                            result: enrollment_1.AcuantResultEnum.Passed,
                            documentClass: enrollment_1.AcuantDocumentClassEnum.Passport,
                            alerts: [
                                {
                                    type: 'Document Expired',
                                    result: enrollment_1.AcuantResultEnum.Passed,
                                },
                            ],
                            documentNameWithoutSeries: 'United States (USA) Passport',
                            scanAttemptNumber: 1,
                            ...data,
                        },
                    }
                });
                const attachmentPhotoIdUploadResponse = await this.enrollmentStepsClient.attachmentPodPhotoIdUpload({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    attachmentPhotoIdCreateForm: {
                        result: enrollment_1.AcuantResultEnum.Passed,
                        documentClass: enrollment_1.AcuantDocumentClassEnum.Passport,
                        alerts: [
                            {
                                type: 'Document Expired',
                                result: enrollment_1.AcuantResultEnum.Passed,
                            },
                        ],
                        acuantResponseJSON: '{}',
                        documentNameWithoutSeries: 'United States (USA) Passport',
                        scanAttemptNumber: 1,
                        purposes: [enrollment_1.AttachmentPhotoIdCreateFormPurposesEnum.PHOTOID],
                        acuantFields: [
                            {
                                name: 'Sex',
                                value: 'F',
                                reliability: 0.1234567,
                            },
                            {
                                name: 'Issuing State Code',
                                value: 'AZ',
                                reliability: 1.0,
                            },
                        ],
                        ambassadorOverride: false,
                        ...data,
                    },
                });
                await this.enrollmentStepsClient.attachmentItemUpload({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    attachmentId: (_a = attachmentPhotoIdUploadResponse === null || attachmentPhotoIdUploadResponse === void 0 ? void 0 : attachmentPhotoIdUploadResponse.attachment) === null || _a === void 0 ? void 0 : _a.id,
                    file: new Blob(),
                    fileName: 'test.jpg',
                    lightSpectrum: enrollment_1.LightSpectrum.INFRARED,
                    side: enrollment_1.AttachmentSideModel.BACK,
                });
                await this.enrollmentStepsClient.attachmentMeta({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    attachmentId: (_b = attachmentPhotoIdUploadResponse === null || attachmentPhotoIdUploadResponse === void 0 ? void 0 : attachmentPhotoIdUploadResponse.attachment) === null || _b === void 0 ? void 0 : _b.id,
                    attachmentMetaForm: {
                        fields: [
                            {
                                name: 'passportNumber',
                                value: '123123123',
                            },
                            {
                                name: 'passportExpirationDate',
                                value: '2024-03-04',
                            },
                        ],
                    },
                });
                return enrollment;
            });
            return this;
        };
        this.acceptAgreement = (data) => {
            this.steps.push(async (config) => {
                await this.enrollmentStepsClient.acceptAgreement({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    agreement: {
                        agreementType: enrollment_1.AgreementAgreementTypeEnum.CERTIFYACCURATEDATA,
                        accepted: true,
                        ...data,
                    },
                });
                return this.enrollmentClient.getStateById({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                });
            });
            return this;
        };
        this.ssn = (data) => {
            this.steps.push((config) => {
                this.logStep('ssn');
                return this.enrollmentStepsClient.provideSsn({
                    enrollmentId: config.enrollmentId,
                    xCLEARCorrelationInfo: config.correlationInfo,
                    ssnStep: {
                        ssn: '123-45-6789',
                        ...data,
                    },
                });
            });
            return this;
        };
        this.compose = async (config) => {
            if (process.env.JEST_WORKER_ID === undefined && !this.suppressLogs) {
                console.warn('Using FluentEnrollmentContextBuilder is for development and testing purposes only. DO NOT use in PROD');
            }
            if (this.steps.length === 0) {
                throw new Error('You must specify at least one step');
            }
            const { enrollment, jwt } = await create_new_enrollment_1.createNewEnrollmentId(this.enrollmentStepsClient, this.getXCLEARCorrelationInfo, this.suppressLogs);
            const finalConfig = {
                enrollmentId: enrollment.id,
                correlationInfo: this.getXCLEARCorrelationInfo(),
                authorization: jwt,
                ...config,
            };
            const taskRunner = async (acc, task) => {
                await acc;
                if (this.timeout)
                    await new Promise((resolve) => setTimeout(() => resolve(true), this.timeout));
                const newState = await task(finalConfig, enrollment).catch(async (error) => {
                    if (error.clone) {
                        const clonedError = error.clone();
                        this.logError('-----------------------------------------------------');
                        this.logError(`Error Response URL: ${clonedError.url}`);
                        this.logError(`Error Code ${clonedError.status}`);
                        if (clonedError.json) {
                            this.logError(`Error Response JSON: ${JSON.stringify(await clonedError.json())}`);
                        }
                        else if (clonedError.text) {
                            this.logError(`Error Response Text: ${JSON.stringify(await clonedError.text())}`);
                        }
                    }
                    else {
                        this.logError(`Error Response: ${JSON.stringify(error)}`);
                    }
                    this.logError('Requesting Task: ', task.toString());
                    this.logError('-----------------------------------------------------');
                    return Promise.reject(error);
                });
                return newState;
            };
            const finalState = this.steps.reduce(taskRunner, Promise.resolve(enrollment));
            return finalState;
        };
        this.enrollmentStepsClient = enrollmentStepsClient;
        this.enrollmentClient = enrollmentClient;
        this.getXCLEARCorrelationInfo = getXCLEARCorrelationInfo;
        this.timeout = timeout;
        this.suppressLogs = suppressLogs;
        this.suppressErrors = suppressErrors;
    }
}
exports.FluentEnrollmentContextBuilder = FluentEnrollmentContextBuilder;
//# sourceMappingURL=FluentEnrollmentContextBuilder.js.map